import React, { Component } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import cn from 'classnames';
import {
  alignmentType, linkType, SANS_SERIF, typographyType, responsiveImageType,
} from '../../propTypes';
import theme, { getFontFamilyAndWeightFor } from '../../styles/theme';
import CtaButton from '../CtaButton';
import Countdown from '../Countdown';
import ContentContainer from './ContentContainer';
import CaptionTitle from './CaptionTitle';
import get from '../../helpers/get';
import createElementResizeDetectorRef from '../../helpers/createElementResizeDetectorRef';

class Caption extends Component {
  state = {
    headerImageWidth: 100,
    height: undefined,
  };

  componentDidMount() {
    this.checkBounds();
  }

  componentDidUpdate() {
    this.checkBounds();
  }

  getPadding = el => {
    const padding = parseInt(window.getComputedStyle(el, null).getPropertyValue('padding-top'), 10) // eslint-disable-line no-undef
      + parseInt(window.getComputedStyle(el, null).getPropertyValue('padding-bottom'), 10); // eslint-disable-line no-undef
    // eslint-disable-next-line no-restricted-globals
    return isNaN(padding) ? 0 : padding;
  };

  setElementResizeRef = createElementResizeDetectorRef(({ el, height }) => {
    const { height: currentHeight } = this.state;
    const padding = this.getPadding(el);
    const heightWithoutPadding = height - padding;
    if (currentHeight && heightWithoutPadding > currentHeight) {
      // Reset the width percent when the width goes up
      this.setState({ headerImageWidth: 100 });
    }
    this.setState({ height: heightWithoutPadding });
  });

  setRef = ref => {
    this.ref = ref;
    this.checkBounds();
  };

  checkBounds = () => {
    const { height, headerImageWidth } = this.state;
    if (this.hasHeaderImage()) {
      if (this.ref
        && height < this.ref.getBoundingClientRect().height
        && headerImageWidth > 10) {
        this.setState({ headerImageWidth: headerImageWidth - 5 });
      }
    }
  };

  hasHeaderImage = () => {
    const { headerImage } = this.props;
    return !!(headerImage && get(headerImage, ['sizes', 0, 'url']));
  };

  renderCopy() {
    const {
      copy, hasActiveCountdown, classes,
    } = this.props;
    return !hasActiveCountdown && copy && (
      <p className={classes.copy}>
        {copy}
      </p>
    );
  }

  renderCountdown() {
    const {
      carouselId, classes, hasActiveCountdown, isCurrent, slideIndex,
    } = this.props;
    return hasActiveCountdown && (
      <Countdown
        className={classes.countdown}
        sliceId={carouselId}
        itemIndex={slideIndex}
        animated={isCurrent}
      />
    );
  }

  renderCta() {
    const {
      classes, cta, hasActiveCountdown, onMouseEnter, onMouseLeave,
    } = this.props;
    return !hasActiveCountdown && cta && (
      <CtaButton
        className={classes.cta}
        link={cta}
        defaultText="Shop now"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  renderTitle() {
    const {
      classes, title, titleTag, headerImage, position,
      typography,
    } = this.props;
    const { headerImageWidth } = this.state;
    return (
      <CaptionTitle
        headerImage={this.hasHeaderImage() ? headerImage : undefined}
        className={classes.titleTypography}
        headerImageWidth={headerImageWidth}
        titleTag={titleTag}
        position={position}
        title={title}
        typography={typography}
      />
    );
  }

  renderSubtitle() {
    const { classes, subtitle } = this.props;
    if (!subtitle) return null;
    return (
      <span className={classes.subtitle}>
        {subtitle}
      </span>
    );
  }

  render() {
    const {
      alignment, classes, className, cta, edgeToEdge, hasActiveCountdown, position, title,
      topBiases, headerImage, contentWidth,
    } = this.props;
    return headerImage || title || cta || hasActiveCountdown ? (
      <ContentContainer
        domRef={this.hasHeaderImage() ? this.setElementResizeRef : undefined}
        alignment={alignment}
        className={className}
        edgeToEdge={edgeToEdge}
        position={position}
        topBiases={topBiases}
        largeContent={contentWidth === 'large' || this.hasHeaderImage()}
      >
        <div
          ref={this.setRef}
          className={cn(classes.caption, edgeToEdge && classes.edgeToEdge)}
        >
          {this.renderSubtitle()}
          {this.renderTitle()}
          {this.renderCopy()}
          {this.renderCountdown()}
          {this.renderCta()}
        </div>
      </ContentContainer>
    ) : null;
  }
}

Caption.propTypes = {
  alignment: alignmentType,
  carouselId: PropTypes.number.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  copy: PropTypes.string,
  cta: linkType,
  edgeToEdge: PropTypes.bool,
  hasActiveCountdown: PropTypes.bool,
  isCurrent: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  position: alignmentType,
  slideIndex: PropTypes.number,
  title: PropTypes.string,
  titleTag: PropTypes.string,
  subtitle: PropTypes.string,
  topBiases: PropTypes.objectOf(PropTypes.number),
  typography: typographyType,
  headerImage: responsiveImageType,
  contentWidth: PropTypes.string,
};

Caption.defaultProps = {
  alignment: undefined,
  className: undefined,
  copy: undefined,
  cta: undefined,
  edgeToEdge: true,
  hasActiveCountdown: false,
  isCurrent: false,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  position: undefined,
  slideIndex: undefined,
  title: undefined,
  titleTag: 'h2',
  subtitle: undefined,
  topBiases: undefined,
  typography: SANS_SERIF,
  headerImage: undefined,
  contentWidth: undefined,
};

export default injectSheet({
  caption: {
    'div& p': {
      fontFamily: theme.fonts.body,
    },
  },
  titleTypography: {
    color: 'inherit',
    lineHeight: '40px',
    fontSize: '28px',
    [theme.breakpoints.up('lg')]: {
      lineHeight: '60px',
      fontSize: '52px',
    },
  },
  copy: {
    fontSize: '12px',
    margin: [4, 0, 0],
    lineHeight: 1.6,
    letterSpacing: '0.015em',
    [theme.breakpoints.up('md')]: {
      fontSize: '16px',
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: '15px',
      fontSize: '20px',
    },
  },
  countdown: {
    extend: 'titleTypography',
  },
  cta: {
    marginTop: '16px',
    [theme.breakpoints.up('md')]: {
      marginTop: '32px',
    },
  },
  subtitle: {
    ...getFontFamilyAndWeightFor('bold'),
    fontSize: 12,
    lineHeight: 1.5,
    letterSpacing: 2.5,
    color: 'currentColor',
    textTransform: 'uppercase',
    marginBottom: 8,
    display: 'block',
    [theme.breakpoints.up('md')]: {
      fontSize: 16,
      marginBottom: theme.spacing.vertical.sm,
    },
  },
})(Caption);
